import React, { useCallback, useEffect, useState } from "react";
import Cookies from "js-cookie";
import { ArrowRight, AlertTriangle, Camera, LogOut } from "lucide-react";
import notify from "../../../components/ACS-Dashboard/ACS-Blocks/Toast";
import {
  fetchMyHostlerJobs,
  fetchClaimableHostlerJobs,
  claimHostlerJob,
  completeHostlerJob,
  reportTrailerIssue,
  resolveTrailerOfficeCodes,
} from "./api";

const ISSUE_TYPES = [
  { key: "Flat tire", safety: true },
  { key: "Damage", safety: true },
  { key: "Leak / spill", safety: true },
  { key: "Wrong trailer", safety: false },
  { key: "Blocked spot", safety: false },
  { key: "Missing paperwork", safety: false },
  { key: "Other", safety: false },
];

function currentUserLabel() {
  try {
    const u = JSON.parse(Cookies.get("userData") || "{}");
    return u.email || u.firstName || "Hostler";
  } catch {
    return "Hostler";
  }
}

function JobCard({ job, onClaim, onComplete, onReportIssue, busy, mode }) {
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
      <div className="mb-1.5 flex items-center justify-between">
        <span className="text-[15px] font-extrabold text-gray-900">{job.trailerNumber}</span>
        {job.issueFlag && (
          <span className="flex items-center gap-1 rounded bg-red-50 px-2 py-0.5 text-[11px] font-semibold text-red-700">
            <AlertTriangle size={12} /> Issue
          </span>
        )}
      </div>
      <div className="mb-1 flex items-center gap-2 text-[13px] font-semibold text-gray-700">
        <span>{job.fromLocationLabel || "Current spot"}</span>
        <ArrowRight size={14} className="text-[#212D69]" />
        <span>{job.toLocationLabel}</span>
      </div>
      <p className="mb-3 text-[11.5px] text-gray-400">
        Drop trailer{job.crossWarehouse ? " · cross-warehouse" : " · same warehouse"}
      </p>
      <div className="flex gap-2">
        {mode === "available" && (
          <button
            type="button"
            disabled={busy}
            onClick={() => onClaim(job)}
            className="flex-1 rounded-lg bg-[#212D69] py-2.5 text-[13px] font-semibold text-white hover:bg-[#324EC6] disabled:opacity-50"
          >
            {busy ? "Claiming…" : "Claim job"}
          </button>
        )}
        {mode === "mine" && (
          <>
            <button
              type="button"
              onClick={() => onReportIssue(job)}
              className="rounded-lg border border-gray-300 px-3 py-2.5 text-[13px] font-semibold text-gray-700 hover:bg-gray-50"
            >
              Report issue
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={() => onComplete(job)}
              className="flex-1 rounded-lg bg-[#212D69] py-2.5 text-[13px] font-semibold text-white hover:bg-[#324EC6] disabled:opacity-50"
            >
              {busy ? "Completing…" : "Mark complete"}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

function ReportIssueSheet({ job, onClose, onSubmit, submitting }) {
  const [issueType, setIssueType] = useState(ISSUE_TYPES[0].key);
  const [notes, setNotes] = useState("");
  const [photo, setPhoto] = useState(null);

  const isSafety = ISSUE_TYPES.find((t) => t.key === issueType)?.safety;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (isSafety && !photo) {
      notify.error({ title: "Photo required", message: "Safety issues require a photo." });
      return;
    }
    onSubmit({ issueType, notes, photo });
  };

  return (
    <div className="fixed inset-0 z-[300] flex items-end justify-center bg-black/40" onClick={onClose}>
      <form
        onSubmit={handleSubmit}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md rounded-t-2xl bg-white p-5 pb-6"
      >
        <div className="mx-auto mb-4 h-1 w-9 rounded-full bg-gray-300" />
        <h3 className="text-[15px] font-bold text-gray-900">Report an issue</h3>
        <p className="mb-3 text-[12px] text-gray-500">
          Trailer {job?.trailerNumber} · {job?.fromLocationLabel} → {job?.toLocationLabel}
        </p>

        <div className="mb-3 flex flex-wrap gap-1.5">
          {ISSUE_TYPES.map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={() => setIssueType(t.key)}
              className={`rounded-full border px-3 py-1.5 text-[12px] font-semibold ${
                issueType === t.key
                  ? "border-red-600 bg-red-600 text-white"
                  : "border-gray-300 bg-white text-gray-700"
              }`}
            >
              {t.key}
            </button>
          ))}
        </div>

        <label className="mb-3 flex cursor-pointer flex-col items-center justify-center rounded-lg border border-dashed border-gray-300 py-4 text-[12px] text-gray-400">
          <Camera size={20} className="mb-1" />
          {photo ? photo.name : `Add a photo${isSafety ? " (required)" : " (optional)"}`}
          <input
            type="file"
            accept="image/*"
            capture="environment"
            className="hidden"
            onChange={(e) => setPhoto(e.target.files?.[0] || null)}
          />
        </label>

        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={3}
          placeholder="What did you find?"
          className="mb-4 w-full rounded-lg border border-gray-300 px-3 py-2 text-[13px]"
        />

        <div className="flex gap-2">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 rounded-lg border border-gray-300 py-2.5 text-[13px] font-semibold text-gray-700"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={submitting}
            className="flex-1 rounded-lg bg-red-600 py-2.5 text-[13px] font-semibold text-white disabled:opacity-50"
          >
            {submitting ? "Submitting…" : "Submit & notify dispatch"}
          </button>
        </div>
      </form>
    </div>
  );
}

/**
 * Standalone, full-screen mobile-friendly Hostler view — registered as a top-level route outside
 * /dashboard/* (no sidebar/header chrome), since a Hostler account only ever holds
 * HOSTLER:VIEW_GET/JOB_PATCH and has nothing else in the full dashboard to see.
 */
export default function HostlerJobsView() {
  const [tab, setTab] = useState("mine");
  const [mine, setMine] = useState([]);
  const [available, setAvailable] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyTrailerId, setBusyTrailerId] = useState(null);
  const [issueJob, setIssueJob] = useState(null);
  const [submittingIssue, setSubmittingIssue] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const officeCodes = resolveTrailerOfficeCodes();
      const [mineData, availableData] = await Promise.all([
        fetchMyHostlerJobs().catch(() => []),
        fetchClaimableHostlerJobs(officeCodes).catch(() => []),
      ]);
      setMine(Array.isArray(mineData) ? mineData : []);
      setAvailable(Array.isArray(availableData) ? availableData : []);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const id = window.setInterval(load, 20000);
    return () => window.clearInterval(id);
  }, [load]);

  const handleClaim = async (job) => {
    setBusyTrailerId(job.trailerId);
    try {
      await claimHostlerJob(job.trailerId);
      notify.success({ title: "Job claimed", message: `${job.trailerNumber} is now yours.` });
      setTab("mine");
      await load();
    } catch (err) {
      notify.error({
        title: "Could not claim job",
        message: err?.response?.data?.message || err?.response?.data?.content || "It may have just been claimed by someone else.",
      });
    } finally {
      setBusyTrailerId(null);
    }
  };

  const handleComplete = async (job) => {
    setBusyTrailerId(job.trailerId);
    try {
      await completeHostlerJob(job.trailerId);
      notify.success({ title: "Job complete", message: `${job.trailerNumber} moved to ${job.toLocationLabel}.` });
      await load();
    } catch (err) {
      notify.error({
        title: "Could not complete job",
        message:
          err?.response?.data?.message ||
          err?.response?.data?.content ||
          "The destination may no longer be available — ask dispatch to check the job.",
      });
    } finally {
      setBusyTrailerId(null);
    }
  };

  const handleReportIssue = async ({ issueType, notes, photo }) => {
    setSubmittingIssue(true);
    try {
      await reportTrailerIssue(issueJob.trailerId, { issueType, notes, photo });
      notify.success({ title: "Issue reported", message: "Dispatch has been notified." });
      setIssueJob(null);
      await load();
    } catch (err) {
      notify.error({
        title: "Could not report issue",
        message: err?.response?.data?.message || err?.response?.data?.content || "Please try again.",
      });
    } finally {
      setSubmittingIssue(false);
    }
  };

  const handleLogout = () => {
    Cookies.remove("authToken");
    window.location.href = "/login";
  };

  const list = tab === "mine" ? mine : available;

  return (
    <div className="flex min-h-screen w-full flex-col bg-[#F8FAFF]">
      <div className="border-b border-gray-200 bg-white px-5 pb-3 pt-5">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-lg font-extrabold text-gray-900">My Jobs</h1>
            <p className="text-[12px] text-gray-500">{currentUserLabel()} · Hostler</p>
          </div>
          <button type="button" onClick={handleLogout} className="rounded p-2 text-gray-400 hover:bg-gray-100" aria-label="Log out">
            <LogOut size={18} />
          </button>
        </div>
        <div className="mt-3 flex rounded-lg bg-gray-100 p-1">
          <button
            type="button"
            onClick={() => setTab("mine")}
            className={`flex-1 rounded-md py-1.5 text-[13px] font-bold ${tab === "mine" ? "bg-white text-gray-900 shadow-sm" : "text-gray-500"}`}
          >
            Assigned to me ({mine.length})
          </button>
          <button
            type="button"
            onClick={() => setTab("available")}
            className={`flex-1 rounded-md py-1.5 text-[13px] font-bold ${tab === "available" ? "bg-white text-gray-900 shadow-sm" : "text-gray-500"}`}
          >
            Available ({available.length})
          </button>
        </div>
      </div>

      <div className="mx-auto flex w-full max-w-md flex-1 flex-col gap-3 overflow-y-auto p-4">
        {loading ? (
          <p className="pt-10 text-center text-[13px] text-gray-400">Loading jobs…</p>
        ) : list.length === 0 ? (
          <p className="pt-10 text-center text-[13px] text-gray-400">
            {tab === "mine" ? "No jobs assigned to you right now." : "No unclaimed jobs at your offices right now."}
          </p>
        ) : (
          list.map((job) => (
            <JobCard
              key={job.trailerId}
              job={job}
              mode={tab}
              busy={busyTrailerId === job.trailerId}
              onClaim={handleClaim}
              onComplete={handleComplete}
              onReportIssue={setIssueJob}
            />
          ))
        )}
      </div>

      {issueJob && (
        <ReportIssueSheet
          job={issueJob}
          onClose={() => setIssueJob(null)}
          onSubmit={handleReportIssue}
          submitting={submittingIssue}
        />
      )}
    </div>
  );
}
